import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSortable } from '@dnd-kit/react/sortable';

import type { EditorCollectionItemDragData } from '@/components/blocks/editor/collection/collection-dnd';
import { getDocument } from '@/lib/utils';
import { shouldDeferCollectionCardPointerSelection } from './collection-card-pointer';

export function useSortableCard({
  id,
  sortableId,
  index,
  group,
  dragData,
  disabled,
  onSelectionChange,
  primarySelected = false,
}: {
  id: string;
  /** Override the sortable registration ID (e.g. for carousel loop duplicates). */
  sortableId?: string;
  index: number;
  group: string;
  dragData: EditorCollectionItemDragData;
  disabled?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  primarySelected?: boolean;
}) {
  const effectiveId = sortableId ?? id;
  const { ref: setElementRef, isDragging } = useSortable({
    id: effectiveId,
    index,
    group,
    data: dragData,
    disabled: Boolean(disabled),
  });

  const [isSelected, setIsSelected] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  // Register only the sortable element — not handleRef. When handle === element, dnd-kit
  // treats every child (including contenteditable TipTap) as a drag activator and blocks
  // text selection. Leaf blocks use the same ref-only pattern.
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setElementRef(node);
      cardRef.current = node;
    },
    [setElementRef],
  );

  useEffect(() => {
    if (!(isSelected || primarySelected) || disabled) return;
    const handlePointerDown = (e: globalThis.PointerEvent) => {
      if (!cardRef.current?.contains(e.target as Node)) {
        setIsSelected(false);
        onSelectionChangeRef.current?.(false);
      }
    };
    getDocument()?.addEventListener('pointerdown', handlePointerDown, true);
    return () => getDocument()?.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isSelected, primarySelected, disabled]);

  const listeners = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled || shouldDeferCollectionCardPointerSelection(e.target)) {
        return;
      }
      setIsSelected(true);
      onSelectionChangeRef.current?.(true);
    },
  };

  return {
    listeners,
    isDragging,
    isSelected,
    mergedRef,
    cardRef,
  };
}
